import { format } from 'date-fns';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ProductCreate } from '../../Inventory/Product';
import { EstimateAndQuoteCreateCustomerSelect } from '../EstimateAndQuote/EstimateAndQuoteCreateCustomerSelect';

import { Box, BoxColumn, BoxRowColumn } from '@components/Box';
import { CollapsibleBoxColumn } from '@components/CollapsibleBoxColumn';
import { FormSectionContainer } from '@components/Container';
import { CustomerDisplayCard } from '@components/CustomerDisplayCard';
import { ExpandingBoxColumn } from '@components/ExpandingBoxColumn';
import {
  FormikDateInput,
  FormikSelect,
  FormikTextArea,
} from '@components/Form';
import { FormikInput } from '@components/Form/FormikInput';
import { FormActionsBar } from '@components/FormActionsBar';
import { LineItemsTable } from '@components/LineItemsTable';
import { Modal } from '@components/Modal';
import { routes } from '@config/routes';
import {
  useNotification,
  useSalesTaxEffectiveRates,
  useCustomerData,
  useProductData,
  useLineItems,
  useFormSubmission,
  useTaxCalculation,
} from '@hooks/index';
import { CustomerViewModel } from '@models/Customer';
import { invoiceDefaultValue, invoiceValidation } from '@models/Invoice';
import {
  invoiceDetailDefaultValue,
  InvoiceDetailViewModel,
} from '@models/InvoiceDetail';
import {
  invoiceDetailSalesTaxDefaultValue,
  InvoiceDetailSalesTaxViewModel,
} from '@models/InvoiceDetailSalesTax';
import { InvoiceService } from '@services/Invoice';

export const InvoiceCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showInfo, showSuccess, showError } = useNotification();
  const previousLocation = location.state?.from || routes.RNC_INVOICES;
  const preSelectedCustomerId = location.state?.customerID ?? null;

  // ===== SHARED HOOKS =====
  // Customer data hook
  const {
    selectedCustomer,
    customerContacts,
    customerBillingAddresses,
    customerShippingAddresses,
    loadSelectedCustomer,
    loadSelectedContact,
  } = useCustomerData({
    onError: showError,
    onCustomerLoaded: (customer) => {
      recalculateTaxAmountsForCustomer(customer);
    },
    onCustomerCleared: () => {
      recalculateTaxAmountsForCustomer(null);
    },
  });

  // Product data hook
  const {
    products,
    loadProducts,
    getProductById,
    refreshProducts: refreshProductsList,
  } = useProductData({
    onError: showError,
  });

  // Sales tax effective rates
  const {
    salesTaxes,
    salesTaxEffectiveRates,
    loadData: loadSalesTaxEffectiveRates,
  } = useSalesTaxEffectiveRates();

  // Line items hook - manages invoice details
  const {
    lineItems: invoiceDetails,
    addLineItem: handleAddInvoiceDetail,
    removeLineItem: handleRemoveInvoiceDetail,
    updateLineItemQuantity,
    updateLineItemSalesTax,
    updateSalesTaxRates,
    recalculateTaxAmounts,
    getSalesTaxesBySalesTaxID,
    updateLineItemFromProduct,
  } = useLineItems<InvoiceDetailViewModel, InvoiceDetailSalesTaxViewModel>(
    {
      defaultLineItem: invoiceDetailDefaultValue,
      defaultSalesTax: invoiceDetailSalesTaxDefaultValue,
      salesTaxesKey: 'invoiceDetailSalesTaxes',
    },
    { onInfo: showInfo }
  );

  // Form submission hook - supports saveAndPost for invoices
  const { setSubmitType, handlePostSubmitNavigation, getSaveMode } =
    useFormSubmission({
      listRoute: routes.RNC_INVOICES,
      getEditRoute: (id) => `${routes.RNC_INVOICES}/${id}/edit`,
      supportsSaveAndPost: true,
    });

  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [isLeftColumnCollapsed, setIsLeftColumnCollapsed] = useState(false);

  // Tax calculation hook - manages tax exempt status
  const { setFormikPropsRef, getIsTaxExempt, handleCustomerTaxExemptChange } =
    useTaxCalculation();

  // Recalculate tax amounts using the hook
  const recalculateTaxAmountsForCustomer = (
    customer: CustomerViewModel | null
  ) => {
    const isTaxExempt = handleCustomerTaxExemptChange(customer);
    recalculateTaxAmounts(isTaxExempt);
  };

  const loadSelectedProduct = async (productId: string, index: number) => {
    const product = await getProductById(Number(productId));
    if (product) {
      const isTaxExempt = getIsTaxExempt();

      // Get effective tax rates for the product
      const productSalesTaxes = product.productSalesTaxes.map((tax) => {
        const effectiveRate =
          salesTaxEffectiveRates.find((rate) => rate.id === tax.salesTaxId)
            ?.rate ?? 0;
        return {
          salesTaxID: tax.salesTaxId,
          salesTaxAbbreviation: tax.salesTax.abbreviation,
          rate: effectiveRate,
        } as InvoiceDetailSalesTaxViewModel;
      });

      updateLineItemFromProduct(
        index,
        {
          productID: product.id,
          description: product.description,
          price: product.price,
          quantity: 1,
          productSalesTaxes: productSalesTaxes,
        },
        isTaxExempt
      );
    }
  };

  // Wrapper to adapt hook's updateLineItemQuantity to existing signature
  const handleUpdateInvoiceDetailQuantity = (
    index: number,
    quantity: number
  ) => {
    const isTaxExempt = getIsTaxExempt();
    updateLineItemQuantity(index, quantity, isTaxExempt);
  };

  // Handle updating sales tax on a line item
  const handleUpdateInvoiceDetailSalesTax = (
    invoiceDetailIndex: number,
    invoiceDetailSalesTaxIndex: number,
    _invoiceDetailSalesTaxesLength: number,
    salesTaxID: number
  ) => {
    const salesTax = salesTaxEffectiveRates.find((o) => o.id == salesTaxID);
    updateLineItemSalesTax(
      invoiceDetailIndex,
      invoiceDetailSalesTaxIndex,
      salesTax,
      getIsTaxExempt()
    );
  };

  // Use hook's updateSalesTaxRates for updating tax rates
  const handleUpdateInvoiceDetailSalesTaxRates = () => {
    updateSalesTaxRates(salesTaxEffectiveRates, getIsTaxExempt());
  };

  useEffect(() => {
    handleUpdateInvoiceDetailSalesTaxRates();
  }, [salesTaxEffectiveRates]);

  return (
    <>
      <Box>
        <div className="pt-2 lg:pb-6 md:pb-4 pb-2">
          <p className="w-full text-center lg:text-2xl md:text-lg text-md font-bold text-primary-900">
            Create Invoice
          </p>
        </div>
        <Formik
          initialValues={invoiceDefaultValue}
          validationSchema={invoiceValidation}
          onSubmit={async (values, { setSubmitting }) => {
            const formattedValues = {
              ...values,
              date: format(new Date(values.date), 'yyyy-MM-dd'),
              dueDate: format(new Date(values.dueDate), 'yyyy-MM-dd'),
              discountType: Number(values.discountType),
              saveMode: getSaveMode(),
            };

            formattedValues.invoiceDetails = invoiceDetails;

            const result = await InvoiceService.create(formattedValues);

            if (result.isSuccess && 'data' in result && result.data?.id) {
              showSuccess('Invoice created successfully.');
              handlePostSubmitNavigation({ success: true, id: result.data.id });
            } else {
              showError(`Failed to create invoice. ${result.errorMessages}`);
            }

            setSubmitting(false);
          }}
        >
          {(formikProps) => {
            const { values, setFieldValue } = formikProps;
            setFormikPropsRef(formikProps);

            useEffect(() => {
              if (preSelectedCustomerId) {
                setFieldValue('customerID', preSelectedCustomerId);
                loadSelectedCustomer(preSelectedCustomerId, formikProps);
              }
            }, [preSelectedCustomerId, setFieldValue]);

            // Load initial data
            useEffect(() => {
              loadProducts();
              loadSalesTaxEffectiveRates(new Date());
            }, []);

            return (
              <>
                <form method="POST" onSubmit={formikProps.handleSubmit}>
                  <FormSectionContainer>
                    {/* Calling Card - Only visible when left column is collapsed */}
                    {isLeftColumnCollapsed && selectedCustomer && (
                      <CustomerDisplayCard
                        selectedCustomer={selectedCustomer}
                        contactID={formikProps.values.contactID}
                        contactEmail={formikProps.values.contactEmail}
                        customerContacts={customerContacts}
                        onEditClick={() => setIsLeftColumnCollapsed(false)}
                      />
                    )}

                    <BoxRowColumn>
                      <CollapsibleBoxColumn
                        isCollapsed={isLeftColumnCollapsed}
                        onToggleCollapse={() =>
                          setIsLeftColumnCollapsed(!isLeftColumnCollapsed)
                        }
                        canCollapse={!!selectedCustomer}
                        collapseTitle="Collapse form"
                        disabledTitle="Please select a customer first"
                      >
                        <EstimateAndQuoteCreateCustomerSelect
                          preSelectedCustomerId={
                            selectedCustomer?.id?.toString() ||
                            formikProps.values.customerID?.toString()
                          }
                          onCustomerSelectChange={(customerId: string) => {
                            loadSelectedCustomer(customerId, formikProps);
                          }}
                        />

                        {selectedCustomer && (
                          <div className="mb-4">
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={
                                  formikProps.values.isTaxExempt || false
                                }
                                onChange={(e) => {
                                  formikProps.setFieldValue(
                                    'isTaxExempt',
                                    e.target.checked
                                  );
                                  // Recalculate tax amounts when tax exemption status changes
                                  setTimeout(() => {
                                    recalculateTaxAmountsForCustomer(
                                      selectedCustomer
                                    );
                                  }, 0);
                                }}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                              />
                              <span className="text-sm font-medium text-gray-700">
                                Tax Exempt
                              </span>
                            </label>
                          </div>
                        )}

                        <FormikInput label="Customer Ref" name="customerRef" />

                        <FormikSelect
                          label="Contact Name"
                          name="contactID"
                          selection={customerContacts}
                          onChange={(
                            event: React.ChangeEvent<HTMLSelectElement>
                          ) => {
                            loadSelectedContact(
                              event.target.value,
                              formikProps
                            );
                          }}
                        />

                        <BoxRowColumn>
                          <BoxColumn>
                            <FormikInput
                              label="Email"
                              name="contactEmail"
                              readonly={true}
                            />
                          </BoxColumn>

                          <BoxColumn>
                            <FormikInput
                              label="Phone"
                              name="contactPhone"
                              readonly={true}
                            />
                          </BoxColumn>
                        </BoxRowColumn>

                        <BoxRowColumn>
                          <BoxColumn>
                            <FormikDateInput
                              label="Date (mm/dd/yyyy)"
                              name="date"
                              type="date"
                              changeHandler={(value: string) => {
                                loadSalesTaxEffectiveRates(new Date(value));
                              }}
                            />
                          </BoxColumn>

                          <BoxColumn>
                            <FormikDateInput
                              label="Due Date (mm/dd/yyyy)"
                              name="dueDate"
                              type="date"
                              onChange={(e: any) =>
                                formikProps.setFieldValue(
                                  'dueDate',
                                  format(new Date(e.target.value), 'MM/dd/yyyy')
                                )
                              }
                            />
                          </BoxColumn>
                        </BoxRowColumn>

                        <FormikSelect
                          label="Billing Address"
                          name="billingAddressID"
                          selection={customerBillingAddresses}
                        />

                        {!formikProps.values.isSameShipping ? (
                          <FormikSelect
                            label="Shipping Address"
                            name="shippingAddressID"
                            selection={customerShippingAddresses}
                          />
                        ) : (
                          <>
                            <label className="block lg:text-sm md:text-sm text-xs font-medium text-primary-700">
                              Shipping Address
                            </label>
                            <i>Same as Billing Address</i>
                          </>
                        )}

                        <FormikTextArea label="Notes" name="notes" />
                      </CollapsibleBoxColumn>

                      <ExpandingBoxColumn isExpanded={isLeftColumnCollapsed}>
                        <div className="py-4">
                          <LineItemsTable
                            lineItems={invoiceDetails}
                            products={products}
                            salesTaxes={salesTaxes}
                            salesTaxesKey="invoiceDetailSalesTaxes"
                            onProductSelect={loadSelectedProduct}
                            onQuantityChange={handleUpdateInvoiceDetailQuantity}
                            onSalesTaxChange={handleUpdateInvoiceDetailSalesTax}
                            onAddLineItem={handleAddInvoiceDetail}
                            onRemoveLineItem={handleRemoveInvoiceDetail}
                            onCreateProductClick={() =>
                              setShowCreateProduct(true)
                            }
                            getSalesTaxesBySalesTaxID={() =>
                              getSalesTaxesBySalesTaxID
                            }
                            formikValues={values}
                          />
                        </div>

                        <div className="flex items-end justify-end h-full">
                          <FormActionsBar
                            onCancel={() => navigate(previousLocation)}
                            isSubmitting={formikProps.isSubmitting}
                            isValid={formikProps.isValid}
                            buttons={[
                              {
                                label: 'Save & Post',
                                submitType: 'saveAndPost',
                              },
                              {
                                label: 'Save & Close',
                                submitType: 'saveAndClose',
                              },
                              { label: 'Save', submitType: 'save' },
                            ]}
                            onSubmitTypeChange={setSubmitType}
                          />
                        </div>
                      </ExpandingBoxColumn>
                    </BoxRowColumn>
                  </FormSectionContainer>
                </form>
              </>
            );
          }}
        </Formik>
      </Box>
      {showCreateProduct && (
        <>
          <Modal show={true}>
            <ProductCreate
              onClose={() => {
                setShowCreateProduct(false);
                refreshProductsList();
              }}
            />
          </Modal>
        </>
      )}
    </>
  );
};
